/**
 * PollutionTaxModal.tsx — T-4 Pollution Tax (R16)
 *
 * Single-actor decision modal. Fires when the current drawer lands on a Policy
 * tile in era >= 3 and the 10% RNG roll passes (triggerPollutionTaxIfRolled).
 *
 * Drawer pays POLLUTION_TAX_AMOUNT tokens → per-seat pollution reduced
 * proportionally by POLLUTION_TAX_REDUCTION points (mirror of ClimateVote math).
 * Drawer may decline (no cost, no benefit).
 * No timer — hot-seat single actor, no urgency needed.
 *
 * VISIBLE WHEN: pendingPollutionTax !== null AND boardAnimationActive === false
 * TEST PATH: era >= 3 → land on Policy tile (repeat until 10% roll passes)
 *            → PollutionTaxModal renders for the drawer only.
 *            Confirm drawer seat label appears; Pay deducts 30T and reduces
 *            pollution proportionally; Decline clears modal with no change.
 */

import type React from "react";
import { seatsInGame } from "../../store/game-end.js";
import { SEAT_COLORS, type SeatIndex, useGameStore } from "../../store/game-store.js";
import styles from "./PollutionTaxModal.module.css";

function seatLabel(seat: SeatIndex): string {
  return `P${seat + 1}`;
}

export function PollutionTaxModal(): React.ReactElement | null {
  const pendingPollutionTax = useGameStore((s) => s.pendingPollutionTax);
  const boardAnimationActive = useGameStore((s) => s.boardAnimationActive);
  const pollution = useGameStore((s) => s.pollution);
  const numPlayers = useGameStore((s) => s.numPlayers);
  const payPollutionTax = useGameStore((s) => s.payPollutionTax);
  const declinePollutionTax = useGameStore((s) => s.declinePollutionTax);

  if (pendingPollutionTax === null || boardAnimationActive) return null;

  const { drawerSeat, taxAmount, reductionAmount, proportionalCuts } = pendingPollutionTax;

  return (
    <div
      className={styles.overlay}
      // biome-ignore lint/a11y/useSemanticElements: <dialog> resets stacking context; custom overlay needed for z-index layering
      role="dialog"
      aria-modal="true"
      aria-label="Pollution Tax"
      data-testid="pollution-tax-modal"
    >
      <div className={styles.panel}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.icon} aria-hidden="true">
            ☁️
          </div>
          <div className={styles.titleBlock}>
            <div className={styles.title}>POLLUTION TAX LEVY</div>
            <div className={styles.prompt}>
              The authorities demand a carbon tax of{" "}
              <span className={styles.highlight}>{taxAmount}T</span>. Pay to reduce global pollution
              by <span className={styles.highlight}>{reductionAmount}</span> points (distributed
              proportionally). Decline at no cost.
            </div>
          </div>
        </div>

        {/* Drawer tag */}
        <div className={styles.drawerRow}>
          <span className={styles.drawerLabel}>Tax collector approaches:</span>
          <span
            className={styles.drawerSeat}
            // biome-ignore lint/style/noNonNullAssertion: SEAT_COLORS always has 4 entries
            style={{ color: SEAT_COLORS[drawerSeat]! }}
          >
            {seatLabel(drawerSeat)}
          </span>
        </div>

        {/* Per-seat pollution preview */}
        <div className={styles.previewTable}>
          <div className={styles.previewHeader}>
            <span>Player</span>
            <span>Current pollution</span>
            <span>After tax</span>
            <span>Reduction</span>
          </div>
          {/* Present seats only (< numPlayers). Absent seats keep INITIAL pollution 0
              and must not show a phantom tax row. Default 4 → all four. */}
          {seatsInGame(numPlayers).map((seat) => {
            const current = pollution[seat] ?? 0;
            const cut = proportionalCuts[seat] ?? 0;
            const after = Math.max(0, current - cut);
            return (
              <div
                key={seat}
                className={styles.previewRow}
                data-testid={`pollution-tax-row-${seat}`}
              >
                <span
                  className={styles.seatLabel}
                  // biome-ignore lint/style/noNonNullAssertion: SEAT_COLORS always has 4 entries
                  style={{ color: SEAT_COLORS[seat]! }}
                >
                  {seatLabel(seat)}
                </span>
                <span className={styles.pollValue}>{current}</span>
                <span className={styles.pollAfter}>{after}</span>
                <span className={cut > 0 ? styles.cutPositive : styles.cutZero}>
                  {cut > 0 ? `−${cut}` : "—"}
                </span>
              </div>
            );
          })}
        </div>

        {/* Action buttons */}
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.btnPay}
            onClick={payPollutionTax}
            data-testid="pollution-tax-pay"
          >
            PAY {taxAmount}T (−{reductionAmount} pollution)
          </button>
          <button
            type="button"
            className={styles.btnDecline}
            onClick={declinePollutionTax}
            data-testid="pollution-tax-decline"
          >
            DECLINE
          </button>
        </div>
      </div>
    </div>
  );
}
